/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';

import {
  GENDER_OPTIONS,
  EMOTION_OPTIONS,
  MASK_OPTIONS,
  TEMPERATURE_TAG_OPTIONS,
} from '@vlabs/api-bindings/src/constants';
import { calcSimilarityPercent } from '@vlabs/shared/utils';
import { Tooltip } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';

import st from './SharedCell.module.sass';

export function useNotificationRenders() {
  const { t } = useTranslation();

  const { lists, handlers } = useSelector(
    (state) => ({
      lists: state.lists.byId || [],
      handlers: state.handlers.byId || [],
    }),
    shallowEqual,
  );

  const renderRangeTag = (gte, lt, from, to) => {
    if (!gte && !lt) return null;
    if (gte && !lt) {
      return (
        <div className={st.CreationRulesTag}>
          <span className={st.TagLabel}>{from}</span>
          <span className={st.TagValue}>{gte}</span>
        </div>
      );
    }
    if (!gte && lt) {
      return (
        <div className={st.CreationRulesTag}>
          <span className={st.TagLabel}>{to}</span>
          <span className={st.TagValue}>{lt}</span>
        </div>
      );
    }
    return (
      <div className={st.CreationRulesTag}>
        <span className={st.TagLabel}>{from}</span>
        <span className={st.TagValue}>{gte}</span>
        <span className={st.TagLabel}>{t('vlNotifier:targets.to')}</span>
        <span className={st.TagValue}>{lt}</span>
      </div>
    );
  };

  const renderOptionTag = (arr, option, translate) => {
    if (!arr?.length) return null;
    const filteredArr = option.raw.filter((opt) => arr.includes(opt.value));
    if (!filteredArr.length) return null;
    const labels = filteredArr.map((item) => item.label).join(', ');
    return (
      <div className={st.CreationRulesTag}>
        <span className={st.TagLabel}>{translate}</span>
        {filteredArr.length === 1
          ? (<span className={st.TagValue}>{filteredArr[0].label}</span>)
          : (
            <>
              <span className={st.TagValue}>{`${filteredArr[0]?.label},`}</span>
              <Tooltip position="bottom" tooltipContent={labels}>
                <span className={st.TagLabel}>{`+${filteredArr.length - 1}`}</span>
              </Tooltip>
            </>
          )}
      </div>
    );
  };

  const renderVATags = (arr, translate) => {
    if (!arr?.length) return null;
    const labels = arr.map((item) => item.label).join(', ');
    return (
      <div className={st.CreationRulesTag}>
        <span className={st.TagLabel}>{translate}</span>
        {arr.length === 1 ? (
          <span className={st.TagValue}>{labels}</span>
        ) : (
          <>
            <span className={st.TagValue}>{`${arr[0].label},`}</span>
            <Tooltip position="bottom" tooltipContent={labels}>
              <span className={st.TagLabel}>{`+${arr.length - 1}`}</span>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  const eventRenders = useMemo(() => ({
    age: (value) => renderRangeTag(
      value.age__gte,
      value.age__lt,
      t('vlNotifier:targets.age from'),
      t('vlNotifier:targets.age to'),
    ),

    temperature: (value) => renderRangeTag(
      value.temperature__gte,
      value.temperature__lt,
      t('vlNotifier:targets.temperature from'),
      t('vlNotifier:targets.temperature to'),
    ),

    similarity: (value) => renderRangeTag(
      calcSimilarityPercent(value.top_similar_object_similarity__gte),
      calcSimilarityPercent(value.top_similar_object_similarity__lt),
      t('vlNotifier:targets.top_match_from'),
      t('vlNotifier:targets.top_match_to'),
    ),

    emotions: (value) => renderOptionTag(value.emotions, EMOTION_OPTIONS, t('emotions_title')),
    masks: (value) => renderOptionTag(value.masks, MASK_OPTIONS, t('medical_mask')),
    tags: (value) => renderOptionTag(value.tags, TEMPERATURE_TAG_OPTIONS, t('tags')),

    gender: (value) => {
      if (!value.gender) return null;
      const gender = GENDER_OPTIONS.raw.find((option) => option.value === value.gender)?.label;
      return (
        <div className={st.CreationRulesTag}>
          <span className={st.TagLabel}>{t('vlNotifier:targets.gender')}</span>
          <span className={st.TagValue}>{gender}</span>
        </div>
      );
    },

    sources: (value) => {
      if (!value.sources?.length) return null;
      const labels = value.sources.join(', ');
      return (
        <div className={st.CreationRulesTag}>
          <span className={st.TagLabel}>{t('source')}</span>
          {value.sources.length === 1
            ? (<span className={st.TagValue}>{value.sources[0]}</span>)
            : (
              <>
                <span className={st.TagValue}>{`${value.sources[0]},`}</span>
                <Tooltip position="bottom" tooltipContent={labels}>
                  <span className={st.TagLabel}>{`+${value.sources.length - 1}`}</span>
                </Tooltip>
              </>
            )}
        </div>
      );
    },

    lists: (value) => {
      if (!value.lists?.length) return null;
      const valueLists = value.lists.map((id) => lists[id]).filter(Boolean);
      if (!valueLists.length) return null;
      const labels = valueLists.map((item) => item.user_data).join(', ');
      return (
        <div className={st.CreationRulesTag}>
          <span className={st.TagLabel}>{t('lists')}</span>
          {valueLists.length === 1
            ? (<span className={st.TagValue}>{labels}</span>)
            : (
              <>
                <span className={st.TagValue}>{`${valueLists[0].user_data},`}</span>
                <Tooltip position="bottom" tooltipContent={labels}>
                  <span className={st.TagLabel}>{`+${valueLists.length - 1}`}</span>
                </Tooltip>
              </>
            )}
        </div>
      );
    },

    handlers: (value) => {
      if (!value.handlers?.length) return null;
      const valueHandlers = value.handlers.map((id) => handlers[id]).filter(Boolean);
      if (!valueHandlers.length) return null;
      const labels = valueHandlers.map((item) => item.description).join(', ');
      return (
        <div className={st.CreationRulesTag}>
          <span className={st.TagLabel}>{t('vlNotifier:targets.handler')}</span>
          {valueHandlers.length === 1
            ? (<span className={st.TagValue}>{labels}</span>)
            : (
              <>
                <span className={st.TagValue}>{`${valueHandlers[0].description},`}</span>
                <Tooltip position="bottom" tooltipContent={labels}>
                  <span className={st.TagLabel}>{`+${valueHandlers.length - 1}`}</span>
                </Tooltip>
              </>
            )}
        </div>
      );
    },
  }), [lists, handlers, t]);

  const vaRenders = useMemo(() => ({
    videoanalytics: (value) => renderVATags(value.videoanalytics, t('video analytics')),
    fireClass: (value) => renderVATags(value.fire_type, t('vlNotifier:filters.fire_type')),
    sources: (value) => renderVATags(value.sources, t('source')),
  }), [t]);

  return [eventRenders, vaRenders];
}
